import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Apple, Check, ImageUp, Smartphone, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { AppMark } from '@/components/layout/app-switcher';
import { AndroidForm, emptyAndroid, emptyIos, IosForm, type AndroidDraft, type IosDraft } from '@/components/credentials/credential-forms';
import { FormField } from '@/components/shared/form-field';
import { LoadingButton } from '@/components/shared/loading-button';
import { PageHeader } from '@/components/shared/page-header';
import { SectionCard } from '@/components/shared/section-card';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { LOGO_ACCEPT, LOGO_SIZE, logoFromFile } from '@/lib/logo';
import { notifyError } from '@/lib/toast';
import { ENVIRONMENTS, type AppDetail, type Environment } from '@/lib/types';
import { urlProblem } from '@/lib/url';
import { cn } from '@/lib/utils';

type EnvDraft = { android: AndroidDraft | null; ios: IosDraft | null };
const STEPS = ['Basics', 'Environments', 'Credentials'] as const;
const ENV_HINT: Record<Environment, string> = { production: 'Live users', staging: 'QA and pre-production' };

export function CreateApp() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [logo, setLogo] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const urlError = urlProblem(url.trim());
  const [envs, setEnvs] = useState<Record<Environment, boolean>>({ production: true, staging: true });
  const [drafts, setDrafts] = useState<Record<Environment, EnvDraft>>({
    production: { android: null, ios: null }, staging: { android: null, ios: null },
  });
  const chosen = ENVIRONMENTS.filter((e) => envs[e]);

  const create = useMutation({
    mutationFn: () =>
      api<AppDetail>('/admin/v1/apps', {
        method: 'POST',
        body: {
          name, description: description || undefined, url: url.trim() || undefined, logo: logo ?? undefined,
          environments: chosen.map((environment) => ({
            environment,
            ...(drafts[environment].android?.service_account_json ? { android: drafts[environment].android } : {}),
            ...(drafts[environment].ios?.private_key_p8 ? { ios: drafts[environment].ios } : {}),
          })),
        },
      }),
    onSuccess: (app) => {
      void qc.invalidateQueries({ queryKey: ['apps'] });
      nav(`/apps/${app.id}/credentials`); // land on the verify buttons
    },
  });

  const setDraft = (env: Environment, patch: Partial<EnvDraft>) => setDrafts((d) => ({ ...d, [env]: { ...d[env], ...patch } }));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="New application" description="Set up an app in three steps. Credentials can also be added later." />
      <ol className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const n = i + 1;
          return (
            // Only steps followed by a connector stretch, so the last step ends at the right edge.
            <li key={s} className={cn('flex items-center gap-2', n < STEPS.length && 'flex-1')}>
              <span className={cn('grid size-7 shrink-0 place-items-center rounded-full border text-xs font-medium',
                step === n ? 'border-primary bg-primary text-primary-foreground' : step > n ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground')}>
                {step > n ? <Check className="size-3.5" /> : n}
              </span>
              <span className={cn('text-sm', step === n ? 'font-medium' : 'text-muted-foreground')}>{s}</span>
              {n < STEPS.length && <span className={cn('h-px flex-1', step > n ? 'bg-primary' : 'bg-border')} />}
            </li>
          );
        })}
      </ol>

      {step === 1 && (
        <SectionCard title="Basics" description="How the app appears in this console.">
          <div className="grid gap-4">
            <FormField label="Name" htmlFor="name"><Input id="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="My Mobile App" /></FormField>
            <FormField label="Description (optional)" htmlFor="desc"><Input id="desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this app is and who uses it" /></FormField>
            <FormField label="Application URL (optional)" htmlFor="app-url" hint="The application's own website, e.g. https://myshop.com." error={urlError ?? undefined}>
              <Input id="app-url" type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://myshop.com" maxLength={2048} />
            </FormField>
            <FormField label="Logo (optional)">
              <div className="flex flex-wrap items-center gap-4">
                <AppMark name={name || '?'} logo={logo} className="size-14 text-xl" />
                <input
                  ref={logoInput}
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="hidden"
                  // Resized in the browser now; the API uploads it to storage when the app is created.
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void logoFromFile(f).then(setLogo, notifyError); }}
                />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => logoInput.current?.click()}><ImageUp /> {logo ? 'Replace' : 'Upload logo'}</Button>
                  {logo && <Button type="button" variant="ghost" size="sm" onClick={() => setLogo(null)}><X /> Remove</Button>}
                </div>
                <p className="w-full text-xs text-muted-foreground">PNG, JPG, WebP, GIF or SVG. Resized to {LOGO_SIZE}×{LOGO_SIZE}; a square image looks best.</p>
              </div>
            </FormField>
            <div className="flex justify-end"><Button disabled={!name.trim() || !!urlError} onClick={() => setStep(2)}>Next</Button></div>
          </div>
        </SectionCard>
      )}

      {step === 2 && (
        <SectionCard title="Environments" description="Each environment has separate credentials, keys and devices.">
          <div className="grid gap-3">
            {ENVIRONMENTS.map((e) => (
              <label key={e} className={cn('flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors', envs[e] && 'border-primary/50 bg-primary/5')}>
                <Checkbox checked={envs[e]} onCheckedChange={(v) => setEnvs({ ...envs, [e]: v === true })} />
                <span className="font-medium capitalize">{e}</span>
                <span className="text-sm text-muted-foreground">{ENV_HINT[e]}</span>
              </label>
            ))}
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button disabled={chosen.length === 0} onClick={() => setStep(3)}>Next</Button>
            </div>
          </div>
        </SectionCard>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Credentials are optional now — you can add or replace them later. They are encrypted on save and never shown again.</p>
          {chosen.map((env) => (
            <SectionCard key={env} title={<span className="capitalize">{env}</span>} actions={env === 'production' ? <ToneBadge tone="amber">Live users</ToneBadge> : null}>
              <div className="grid gap-5">
                <PlatformToggle icon={<Smartphone className="size-4" />} label="Android (FCM)" on={!!drafts[env].android} onToggle={(on) => setDraft(env, { android: on ? emptyAndroid() : null })}>
                  {drafts[env].android && <AndroidForm value={drafts[env].android!} onChange={(v) => setDraft(env, { android: v })} />}
                </PlatformToggle>
                <PlatformToggle icon={<Apple className="size-4" />} label="iOS (APNs)" on={!!drafts[env].ios} onToggle={(on) => setDraft(env, { ios: on ? emptyIos() : null })}>
                  {drafts[env].ios && <IosForm value={drafts[env].ios!} onChange={(v) => setDraft(env, { ios: v })} />}
                </PlatformToggle>
              </div>
            </SectionCard>
          ))}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
            <LoadingButton loading={create.isPending} onClick={() => create.mutate()}>Create application</LoadingButton>
          </div>
        </div>
      )}
    </div>
  );
}

function PlatformToggle({ icon, label, on, onToggle, children }: { icon: ReactNode; label: string; on: boolean; onToggle(on: boolean): void; children: ReactNode }) {
  return (
    <div className="grid gap-3 rounded-lg border p-4">
      <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-medium">
        <span className="inline-flex items-center gap-2">{icon} {label}</span>
        <Switch checked={on} onCheckedChange={onToggle} />
      </label>
      {children}
    </div>
  );
}
